import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { toast } from 'react-toastify';
import { connectionsAPI, samlAPI } from '../services/api';
import './ConnectionManager.css';

const ConnectionManager = forwardRef(({ 
  connections, 
  selectedConnection, 
  onConnectionChange, 
  onConnectionsUpdate,
  onDelete: onDeleteProp 
}, ref) => {
  const [showForm, setShowForm] = useState(false);
  const [editingConnectionId, setEditingConnectionId] = useState(null);
  const [authType, setAuthType] = useState('local'); // 'local' or 'sso'
  const [formData, setFormData] = useState({
    name: '',
    hcm_url: '',
    username: '',
    password: '',
    hcm_bearer_token: '',
  });
  const [loading, setLoading] = useState(false);
  const [samlLoading, setSamlLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (authType === 'sso') {
      // If username is not filled, initiate SSO flow
      if (!formData.username) {
        return handleSSOInitiate();
      }
      // If username is filled (after SSO callback), save the connection
      if (!formData.name || !formData.hcm_url || !formData.username || !formData.password) {
        toast.error('Please fill in all fields');
        return;
      }
    } else {
      // Local auth - validate all fields
      if (!formData.name || !formData.hcm_url || !formData.username || !formData.password) {
        toast.error('Please fill in all fields');
        return;
      }
    }

    setLoading(true);
    try {
      const connectionData = {
        ...formData,
        auth_type: authType
      };
      if (editingConnectionId) {
        await connectionsAPI.update(editingConnectionId, connectionData);
        toast.success('Connection updated successfully');
      } else {
        await connectionsAPI.create(connectionData);
        toast.success('Connection created successfully');
      }
      setFormData({ name: '', hcm_url: '', username: '', password: '', hcm_bearer_token: '' });
      setEditingConnectionId(null);
      setAuthType('local');
      setShowForm(false);
      onConnectionsUpdate();
    } catch (error) {
      toast.error(error.response?.data?.error || `Failed to ${editingConnectionId ? 'update' : 'create'} connection`);
    } finally {
      setLoading(false);
    }
  };

  const handleSSOInitiate = async () => {
    if (!formData.name || !formData.hcm_url) {
      toast.error('Please enter connection name and HCM URL');
      return;
    }

    setSamlLoading(true);
    try {
      console.log('[SAML] Initiating SSO with:', { name: formData.name, hcm_url: formData.hcm_url });
      const response = await samlAPI.initiate(formData.name, formData.hcm_url);
      console.log('[SAML] Response:', response.data);
      
      if (response.data?.authUrl) {
        console.log('[SAML] Redirecting to:', response.data.authUrl);
        // Redirect to SAML SSO URL
        window.location.href = response.data.authUrl;
      } else {
        console.error('[SAML] No authUrl in response:', response.data);
        toast.error('Failed to initiate SAML SSO: No authentication URL received');
        setSamlLoading(false);
      }
    } catch (error) {
      console.error('[SAML] Error:', error);
      console.error('[SAML] Error response:', error.response);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to initiate Single Sign On';
      toast.error(`Failed to initiate Single Sign On: ${errorMessage}`);
      setSamlLoading(false);
    }
  };

  const handleEdit = (connection) => {
    setFormData({
      name: connection.name,
      hcm_url: connection.hcm_url,
      username: connection.username,
      password: '', // Don't pre-fill password for security
      hcm_bearer_token: '', // Don't pre-fill token
    });
    setEditingConnectionId(connection.id);
    setAuthType(connection.auth_type === 'sso' ? 'sso' : 'local');
    setShowForm(true);
  };

  const handleDelete = async (connectionId) => {
    if (!window.confirm('Are you sure you want to delete this connection?')) {
      return;
    }

    try {
      if (onDeleteProp) {
        await onDeleteProp(connectionId);
      } else {
        await connectionsAPI.delete(connectionId);
        onConnectionsUpdate();
      }
      toast.success('Connection deleted');
      if (selectedConnection?.id === connectionId) {
        onConnectionChange(null);
      }
    } catch (error) {
      toast.error('Failed to delete connection');
    }
  };

  useImperativeHandle(ref, () => ({
    openEdit: (connection) => handleEdit(connection),
    openAdd: () => {
      setFormData({ name: '', hcm_url: '', username: '', password: '', hcm_bearer_token: '' });
      setEditingConnectionId(null);
      setShowForm(true);
    }
  }), []);


  const handleCloseModal = () => {
    setShowForm(false);
    setFormData({ name: '', hcm_url: '', username: '', password: '', hcm_bearer_token: '' });
    setEditingConnectionId(null);
    setAuthType('local');
  };

  // Handle SAML callback from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const samlUsername = urlParams.get('saml_username');
    const samlPassword = urlParams.get('saml_password');
    const connectionName = urlParams.get('connection_name');
    const hcmUrl = urlParams.get('hcm_url');
    const samlError = urlParams.get('saml_error');

    if (samlError) {
      toast.error(decodeURIComponent(samlError));
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (samlUsername && connectionName && hcmUrl) {
      // Pre-fill form with SAML data (username and password if available)
      setFormData({
        name: connectionName,
        hcm_url: hcmUrl,
        username: samlUsername,
        password: samlPassword || '', // Use password from SAML token if available
      });
      setAuthType('sso');
      setShowForm(true);
      
      if (samlPassword) {
        toast.success('Single Sign On successful! Username and password extracted from token. Click Save to complete the connection.');
        // Auto-save if password is available
        setTimeout(() => {
          const connectionData = {
            name: connectionName,
            hcm_url: hcmUrl,
            username: samlUsername,
            password: samlPassword,
            auth_type: 'sso'
          };
          connectionsAPI.create(connectionData)
            .then(() => {
              toast.success('Connection created successfully');
              setFormData({ name: '', hcm_url: '', username: '', password: '', hcm_bearer_token: '' });
              setAuthType('local');
              setShowForm(false);
              onConnectionsUpdate();
            })
            .catch((error) => {
              toast.error(error.response?.data?.error || 'Failed to create connection');
            });
        }, 500);
      } else {
        toast.success('Single Sign On successful! Please enter your password to complete the connection.');
      }
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [onConnectionsUpdate]);

  // Close modal on Escape key
  useEffect(() => {
    if (!showForm) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleCloseModal();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showForm]);

  return (
    <div className="connection-manager">
      <div style={{ display: 'flex', alignItems: 'center', gap: '67px', marginBottom: '3px' }}>
        <span style={{ color: '#e2e8f0', fontSize: '17px', fontWeight: 600 }}>Connections</span>
        <button
          className="btn-add-connection"
          onClick={() => {
            setFormData({ name: '', hcm_url: '', username: '', password: '', hcm_bearer_token: '' });
            setEditingConnectionId(null);
            setShowForm(true);
          }}
          title="Add Connection"
        >
          +
        </button>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>{editingConnectionId ? 'Edit Connection' : 'New Connection'}</h4>
              <button className="btn-close-modal" onClick={handleCloseModal} title="Close">
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="auth-type-selector">
                <label>Authentication Type:</label>
                <div className="auth-type-buttons">
                  <button
                    type="button"
                    className={`auth-type-btn ${authType === 'local' ? 'active' : ''}`}
                    onClick={() => setAuthType('local')}
                  >
                    Local
                  </button>
                  <button
                    type="button"
                    className={`auth-type-btn ${authType === 'sso' ? 'active' : ''}`}
                    onClick={() => setAuthType('sso')}
                  >
                    Single Sign On
                  </button>
                </div>
              </div>

              <input
                type="text"
                placeholder="Connection Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
              <input
                type="url"
                placeholder="Fusion/HCM URL (e.g. xxx.fa.us2.oraclecloud.com) - NOT identity.oraclecloud.com"
                value={formData.hcm_url}
                onChange={(e) => setFormData({ ...formData, hcm_url: e.target.value })}
                required
              />

              {authType === 'local' ? (
                <>
                  <input
                    type="text"
                    placeholder="Username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                  />
                  <input
                    type="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                  <input
                    type="password"
                    placeholder="HCM REST OAuth token (optional — use when Basic auth is disabled)"
                    value={formData.hcm_bearer_token}
                    onChange={(e) => setFormData({ ...formData, hcm_bearer_token: e.target.value })}
                    title="When your Fusion instance blocks Basic auth for REST, paste an OAuth access token here. Leave blank to use username/password."
                  />
                </>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="Username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    disabled={!!formData.username}
                    required
                  />
                  <input
                    type="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                  <input
                    type="password"
                    placeholder="HCM REST OAuth token (optional)"
                    value={formData.hcm_bearer_token}
                    onChange={(e) => setFormData({ ...formData, hcm_bearer_token: e.target.value })}
                  />
                  {!formData.username && (
                    <button
                      type="button"
                      className="btn-sso-initiate"
                      onClick={handleSSOInitiate}
                      disabled={samlLoading || !formData.name || !formData.hcm_url}
                    >
                      {samlLoading ? 'Redirecting...' : 'Connect with Single Sign On'}
                    </button>
                  )}
                </>
              )}

              <div className="form-actions">
                {authType === 'local' ? (
                  <>
                    <button type="submit" className="btn-save" disabled={loading}>
                      {loading ? (editingConnectionId ? 'Updating...' : 'Saving...') : (editingConnectionId ? 'Update' : 'Save')}
                    </button>
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={handleCloseModal}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button 
                      type="submit" 
                      className="btn-save" 
                      disabled={loading || !formData.name || !formData.hcm_url || !formData.username || !formData.password}
                    >
                      {loading ? 'Saving...' : (editingConnectionId ? 'Update' : 'Save Connection')}
                    </button>
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={handleCloseModal}
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="connections-list">
        {connections.length === 0 && !showForm && (
          <div className="empty-state">
            <p>No connections yet</p>
            <p className="hint">Click "Add Connection" to get started</p>
          </div>
        )}

        {connections.map((conn) => (
          <div
            key={conn.id}
            className={`connection-item ${selectedConnection?.id === conn.id ? 'active' : ''}`}
          >
            <div
              className="connection-info"
              onClick={() => onConnectionChange(conn)}
            >
              <div className="connection-name">{conn.name}</div>
            </div>
            <div className="connection-actions">
              <button
                className="btn-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(conn);
                }}
                title="Edit Connection"
              >
                ✏️
              </button>
              <button
                className="btn-icon btn-delete"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(conn.id);
                }}
                title="Delete Connection"
              >
                🗑️
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

ConnectionManager.displayName = 'ConnectionManager';

export default ConnectionManager;

